import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen, Heart, MessageCircle, Shield, Users, Sparkles, Flame, Wallet, Compass,
  CheckCircle2, Clock, Lock, ArrowRight, Copy, Check, UserPlus, X, ChevronDown, Share2
} from "lucide-react";
import { toast } from "sonner";
import { ChatPanel } from "@/components/ChatPanel";

const ICON_MAP = {
  BookOpen, Heart, MessageCircle, Shield, Users, Sparkles, Flame, Wallet, Compass,
};

export default function Dashboard() {
  const { user, couple, partner, role, loading: authLoading, refresh } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [pastorCopied, setPastorCopied] = useState(false);
  const [pastors, setPastors] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [showPastor, setShowPastor] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (user?.account_type === "pastor") {
      nav("/pastor");
      return;
    }
    if (user?.account_type === "admin") {
      nav("/admin");
      return;
    }
    if (!couple) {
      nav("/onboarding");
      return;
    }
    Promise.all([
      api.get("/modules"),
      api.get("/couples/pastors"),
    ]).then(([m, p]) => {
      setModules(m.data);
      setPastors(p.data);
      setLoading(false);
    });
  }, [couple, user, authLoading, nav]);

  useEffect(() => {
    if (!couple?.id || pastors.length === 0) return;
    const loadUnread = () =>
      api
        .get(`/chat/${couple.id}/summary`)
        .then(({ data }) => setChatUnread(data?.total || 0))
        .catch(() => {});
    loadUnread();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, [couple?.id, pastors.length]);

  if (!couple) return null;

  const partnerJoined = !!couple.partner_b_id;
  const isSolo = !!couple.is_solo;
  const soloOrJoined = partnerJoined || isSolo;
  const fullPack = !!couple.entitlements?.full_pack;
  const profileTagline = isSolo
    ? "Auto-évaluez-vous"
    : { married: "Faites le point ensemble", considering: "Évaluez votre compatibilité", engaged: "Préparez votre mariage" }[couple.relationship_stage] || "Votre parcours à deux";
  const completedCount = modules.filter((m) => m.status.analysis_ready).length;
  const avgScore = modules
    .filter((m) => m.status.compatibility_score !== null && m.status.compatibility_score !== undefined)
    .map((m) => m.status.compatibility_score);
  const avg = avgScore.length ? Math.round(avgScore.reduce((a, b) => a + b, 0) / avgScore.length) : null;

  const copyCode = () => {
    navigator.clipboard.writeText(couple.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyPastorCode = () => {
    navigator.clipboard.writeText(couple.pastor_code);
    setPastorCopied(true);
    setTimeout(() => setPastorCopied(false), 2000);
  };

  const removePastor = async (pastorId) => {
    if (!window.confirm("Retirer ce pasteur de votre parcours ?")) return;
    try {
      await api.post(`/couples/pastors/remove/${pastorId}`);
      setPastors((p) => p.filter((x) => x.id !== pastorId));
      toast.success("Pasteur retiré.");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const buyPack = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        item_type: "pack",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer le paiement.");
      setCheckingOut(false);
    }
  };

  const leaveCouple = async () => {
    if (
      !window.confirm(
        "Vous vouliez plutôt rejoindre votre partenaire ? Ceci supprimera ce couple (personne ne l'a encore rejoint) et vous pourrez saisir son code."
      )
    )
      return;
    try {
      await api.post("/couples/leave");
      await refresh();
      nav("/onboarding");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    }
  };

  const shareInvite = async () => {
    const url = window.location.origin;
    const text =
      `Rejoins-moi sur Classes Prénuptiales 💛\n` +
      `Crée ton compte sur ${url}, puis saisis le code ${couple.invite_code} pour rejoindre notre parcours de préparation au mariage.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Classes Prénuptiales", text });
      } catch (e) {
        /* annulé */
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Message copié — colle-le dans WhatsApp, SMS ou email.");
      } catch (e) {
        toast.error("Impossible de partager sur cet appareil.");
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <p data-testid="profile-tagline" className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
            {profileTagline}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            Bonjour <span className="italic">{user?.name}</span>
          </h1>
          {partnerJoined ? (
            <p className="mt-4 text-[#5C5754] text-lg" data-testid="couple-status-joined">
              En route avec <span className="font-medium">{partner?.name}</span>.
              {completedCount > 0 && ` ${completedCount} module${completedCount > 1 ? "s" : ""} achevé${completedCount > 1 ? "s" : ""}.`}
            </p>
          ) : isSolo ? (
            <p className="mt-4 text-[#5C5754] text-lg" data-testid="solo-status">
              Votre parcours d'auto-analyse personnel.
              {completedCount > 0 && ` ${completedCount} bilan${completedCount > 1 ? "s" : ""} généré${completedCount > 1 ? "s" : ""}.`}
            </p>
          ) : (
            <p className="mt-4 text-[#5C5754] text-lg" data-testid="couple-status-waiting">
              En attente de votre partenaire…
            </p>
          )}
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
          {!partnerJoined && !isSolo && (
            <div className="col-span-2 bg-[#F3EFEB] border border-[#E6DCD3] rounded-2xl p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-2">
                Code à partager
              </p>
              <p
                data-testid="dashboard-invite-code"
                className="font-heading text-3xl tracking-[0.2em] text-[#C86B5E]"
              >
                {couple.invite_code}
              </p>
              <div className="mt-3 flex items-center justify-center gap-4">
                <button
                  data-testid="dashboard-share-code"
                  onClick={shareInvite}
                  className="inline-flex items-center gap-2 text-xs text-white bg-[#C86B5E] hover:bg-[#B35A4E] rounded-full px-4 py-2 transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" /> Partager
                </button>
                <button
                  data-testid="dashboard-copy-code"
                  onClick={copyCode}
                  className="inline-flex items-center gap-2 text-xs text-[#5C5754] hover:text-[#C86B5E]"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-[#E6DCD3]">
                <button
                  data-testid="leave-couple-link"
                  onClick={leaveCouple}
                  className="text-xs text-[#8A8480] hover:text-[#C86B5E] transition-colors"
                >
                  Vous vouliez plutôt <span className="underline">rejoindre votre partenaire avec son code</span> ?
                </button>
              </div>
            </div>
          )}
          {soloOrJoined && (
            <>
              <div className="bg-white border border-[#E6E2DD] rounded-2xl p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-2">
                  Progression
                </p>
                <p className="font-heading text-4xl text-[#2C2A29]">
                  {completedCount}
                  <span className="text-xl text-[#8A8480]">/{modules.length}</span>
                </p>
              </div>
              <div className="bg-white border border-[#E6E2DD] rounded-2xl p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-2">
                  {isSolo ? "Clarté" : "Compatibilité"}
                </p>
                <p className="font-heading text-4xl text-[#7A8B76]">
                  {avg !== null ? `${avg}%` : "—"}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pastor invitation section */}
      {soloOrJoined && (
        <div className="mb-12 bg-[#F3EFEB] border border-[#E6DCD3] rounded-2xl p-6 md:p-8">
          <button
            data-testid="toggle-pastor-section"
            onClick={() => setShowPastor((v) => !v)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-heading text-xl leading-tight">
                  {pastors.length > 0
                    ? `${pastors.length} pasteur${pastors.length > 1 ? "s" : ""} vous accompagne${pastors.length > 1 ? "nt" : ""}`
                    : "Inviter un pasteur / conseiller"}
                </p>
                <p className="text-xs text-[#8A8480] mt-1">
                  Optionnel · Ils accèdent aux analyses et peuvent échanger avec vous par chat
                </p>
              </div>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-[#8A8480] transition-transform ${showPastor ? "rotate-180" : ""}`}
            />
          </button>
          {showPastor && (
            <div className="mt-6 pt-6 border-t border-[#E6DCD3] animate-fade-in-up">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] font-semibold mb-3">
                    Code pasteur à partager
                  </p>
                  <p
                    data-testid="pastor-invite-code"
                    className="font-heading text-3xl tracking-[0.2em] text-[#C86B5E]"
                  >
                    {couple.pastor_code}
                  </p>
                  <button
                    data-testid="copy-pastor-code"
                    onClick={copyPastorCode}
                    className="mt-3 inline-flex items-center gap-2 text-xs text-[#5C5754] hover:text-[#C86B5E]"
                  >
                    {pastorCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {pastorCopied ? "Copié" : "Copier le code"}
                  </button>
                  <p className="mt-4 text-sm text-[#5C5754] leading-relaxed">
                    Partagez ce code avec le pasteur/conseiller de votre choix. Il devra créer
                    un compte "Pasteur" puis entrer ce code pour vous suivre.
                  </p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] font-semibold mb-3">
                    Pasteurs qui vous suivent
                  </p>
                  {pastors.length === 0 ? (
                    <p className="text-sm text-[#8A8480] italic">
                      Aucun pasteur pour l'instant
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {pastors.map((p) => (
                        <li
                          key={p.id}
                          data-testid={`pastor-item-${p.id}`}
                          className="flex items-center justify-between bg-white border border-[#E6E2DD] rounded-xl px-4 py-3"
                        >
                          <div>
                            <p className="font-medium text-[#2C2A29]">{p.name}</p>
                            <p className="text-xs text-[#8A8480]">{p.email}</p>
                          </div>
                          <button
                            data-testid={`remove-pastor-${p.id}`}
                            onClick={() => removePastor(p.id)}
                            className="text-[#8A8480] hover:text-[#C86B5E] p-1"
                            title="Retirer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chat général avec le pasteur */}
      {soloOrJoined && pastors.length > 0 && (
        <div className="mb-12" data-testid="couple-general-chat">
          <div className="flex items-center gap-3 mb-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold">
              Échanger avec votre pasteur
            </p>
            {chatUnread > 0 && (
              <span
                data-testid="couple-chat-unread-badge"
                className="inline-flex items-center gap-1 bg-[#C86B5E] text-white text-xs font-semibold rounded-full px-2.5 py-0.5"
              >
                <MessageCircle className="w-3 h-3" /> {chatUnread}
              </span>
            )}
          </div>
          <ChatPanel coupleId={couple.id} scope="general" scopeLabel="Conversation générale" />
        </div>
      )}

      {/* Full pack CTA */}
      {soloOrJoined && !fullPack && (
        <div
          data-testid="full-pack-cta"
          className="mb-12 bg-[#2C2A29] text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
        >
          <div className="flex-1">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4A373] font-semibold mb-2">
              {isSolo ? "Offre solo" : "Offre couple"}
            </p>
            <p className="font-heading text-2xl md:text-3xl leading-snug mb-2">
              {isSolo ? (
                <>Débloquez vos 9 bilans · <span className="italic">Pack solo</span></>
              ) : (
                <>Débloquez les 9 analyses · <span className="italic">Pack complet</span></>
              )}
            </p>
            <p className="text-sm text-white/70 leading-relaxed">
              {isSolo
                ? `Le module « Communication » est offert. Débloquez un bilan à ${"3,99€"}, ou l'intégralité du parcours en une fois.`
                : "Le module « Communication » est offert. Débloquez l'analyse d'un module à 5,99€, ou l'intégralité du parcours en une fois."}
            </p>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <p className="font-heading text-4xl">{isSolo ? "19€" : "29€"}</p>
              {!isSolo && <p className="text-xs text-white/50">au lieu de 54€</p>}
            </div>
            <button
              data-testid="buy-full-pack-button"
              onClick={buyPack}
              disabled={checkingOut}
              className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-8 py-4 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
            >
              {checkingOut ? "Redirection…" : "Débloquer le pack"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {soloOrJoined && fullPack && (
        <div
          data-testid="full-pack-owned"
          className="mb-12 bg-[#7A8B76]/10 border border-[#7A8B76]/30 rounded-2xl p-5 flex items-center gap-3"
        >
          <Sparkles className="w-5 h-5 text-[#7A8B76]" strokeWidth={1.5} />
          <p className="text-[#5C5754]">
            <span className="font-medium text-[#2C2A29]">Pack complet actif</span> — toutes les analyses sont débloquées.
          </p>
        </div>
      )}

      {/* Progression banner */}
      {soloOrJoined && !loading && modules.length > 0 && (
        <div data-testid="progress-banner" className="mb-10 bg-white border border-[#E6E2DD] rounded-2xl p-6 md:p-7">
          <div className="flex items-end justify-between gap-4 mb-3 flex-wrap">
            <div>
              <p className="font-heading text-2xl text-[#2C2A29] leading-none">
                {completedCount === 0
                  ? "Démarrez avec le module gratuit « Communication »."
                  : completedCount >= modules.length
                  ? "Vous avez parcouru tous les modules. Félicitations !"
                  : isSolo
                  ? "Continuez votre auto-analyse, un module après l'autre."
                  : "Continuez votre parcours à deux, un module après l'autre."}
              </p>
              <p className="mt-2 text-sm text-[#8A8480]" data-testid="progress-count">
                {completedCount} module{completedCount > 1 ? "s" : ""} sur {modules.length} complété{completedCount > 1 ? "s" : ""}
              </p>
            </div>
            <p className="font-heading text-4xl text-[#C86B5E] leading-none">
              {Math.round((completedCount / modules.length) * 100)}%
            </p>
          </div>
          <div className="h-2 rounded-full bg-[#F3EFEB] overflow-hidden">
            <div
              className="h-full bg-[#C86B5E] rounded-full transition-all duration-700"
              style={{ width: `${Math.round((completedCount / modules.length) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Modules grid */}
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
          Modules
        </p>
        {loading ? (
          <p className="text-[#8A8480]">Chargement…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((m, i) => {
              const Icon = ICON_MAP[m.icon] || BookOpen;
              const myDone = isSolo ? m.status.partner_a_done : (role === "a" ? m.status.partner_a_done : m.status.partner_b_done);
              const partnerDone = isSolo ? true : (role === "a" ? m.status.partner_b_done : m.status.partner_a_done);
              const ready = m.status.analysis_ready;
              const lockPrice = isSolo ? "3,99€" : "5,99€";
              return (
                <Link
                  to={`/module/${m.id}`}
                  key={m.id}
                  data-testid={`module-card-${m.id}`}
                  className="group bg-white border border-[#E6E2DD] rounded-2xl p-6 hover:border-[#C86B5E]/40 hover:shadow-lg transition-all animate-fade-in-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-[#F3EFEB] flex items-center justify-center group-hover:bg-[#C86B5E]/10 transition-colors">
                      <Icon className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
                    </div>
                    <div className="flex items-center gap-2">
                      {m.status.is_free ? (
                        <span
                          data-testid={`badge-free-${m.id}`}
                          className="text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#7A8B76]/15 text-[#7A8B76]"
                        >
                          Gratuit
                        </span>
                      ) : m.status.unlocked ? (
                        <span
                          data-testid={`badge-unlocked-${m.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#7A8B76]/15 text-[#7A8B76]"
                        >
                          <Check className="w-3 h-3" /> Débloqué
                        </span>
                      ) : (
                        <span
                          data-testid={`badge-locked-${m.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#F3EFEB] text-[#8A8480]"
                        >
                          <Lock className="w-3 h-3" /> {lockPrice}
                        </span>
                      )}
                      <span className="font-heading text-sm text-[#8A8480]">
                        {String(m.order).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading text-2xl leading-snug mb-2">{m.title}</h3>
                  <p className="text-sm text-[#5C5754] mb-6 leading-relaxed line-clamp-2">
                    {m.subtitle}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-[#F3EFEB]">
                    <div className="flex items-center gap-2 text-xs">
                      {ready ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-[#7A8B76]" />
                          <span className="text-[#7A8B76] font-medium">
                            Analyse disponible{m.status.compatibility_score !== null ? ` · ${m.status.compatibility_score}%` : ""}
                          </span>
                        </>
                      ) : myDone && partnerDone ? (
                        m.status.unlocked ? (
                          <>
                            <Clock className="w-4 h-4 text-[#D4A373]" />
                            <span className="text-[#5C5754]">Prêt à générer</span>
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4 text-[#C86B5E]" />
                            <span className="text-[#5C5754]">{lockPrice} pour débloquer {isSolo ? "le bilan" : "l'analyse"}</span>
                          </>
                        )
                      ) : myDone ? (
                        <>
                          <Clock className="w-4 h-4 text-[#D4A373]" />
                          <span className="text-[#5C5754]">En attente du partenaire</span>
                        </>
                      ) : partnerDone ? (
                        <>
                          <Clock className="w-4 h-4 text-[#C86B5E]" />
                          <span className="text-[#5C5754]">À ton tour</span>
                        </>
                      ) : (
                        <>
                          <span className="text-[#8A8480]">À commencer</span>
                        </>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#8A8480] group-hover:text-[#C86B5E] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
